import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  Shield,
  Users,
  BarChart3,
  Code,
  Bug,
  Target,
  Briefcase,
  Network,
  GraduationCap,
  Brain,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { services, stats, approach, companyInfo, clients } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import WorldMap from '@/components/WorldMap';

const iconMap = { Shield, Users, BarChart3, Code, Bug, Target, Briefcase, Network, GraduationCap, Brain };

const ClientLogo = ({ client }) => {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div
      className="flex items-center justify-center h-20 px-4 rounded-lg border border-slate-100 bg-[#FAFBFC] hover:border-slate-200 hover:bg-white transition-colors duration-300 group"
      title={client.name}
    >
      {!imgFailed ? (
        <img
          src={client.logo}
          alt={client.name}
          className="max-h-10 max-w-[100px] object-contain opacity-60 group-hover:opacity-100 transition-opacity duration-300"
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="text-[11px] font-medium text-slate-500 text-center leading-tight tracking-wide">
          {client.name}
        </span>
      )}
    </div>
  );
};

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-screen flex items-center bg-[#0B1D33] overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1D33] via-[#0B1D33]/95 to-[#0F2440]/90" />
        {/* Accent decorations */}
        <div className="absolute top-1/4 right-0 w-96 h-96 rounded-full bg-[#3B82A0]/5 blur-3xl" />
        <div className="absolute bottom-0 left-0 right-0 accent-line" />

        {/* World Map - positioned behind content */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <WorldMap className="w-[95%] md:w-[90%] lg:w-[80%] h-auto opacity-60 md:opacity-80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24 lg:pt-0 lg:pb-0 w-full">
          <div className="max-w-3xl">
            <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-6">
              Cybersecurity Advisory
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.25rem] font-light text-white leading-[1.15] mb-8 tracking-tight">
              {companyInfo.heroHeadline}
            </h1>
            <p className="text-base text-slate-400 leading-relaxed mb-10 max-w-2xl font-light">
              {companyInfo.heroDescription}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="bg-[#3B82A0] hover:bg-[#2D6A85] text-white px-7 h-11 text-[13px] font-normal tracking-wide rounded transition-colors duration-200">
                  {companyInfo.ctaPrimary}
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="border-slate-500/30 text-slate-300 hover:bg-white/5 hover:border-slate-400 px-7 h-11 text-[13px] font-normal tracking-wide rounded transition-colors duration-200"
                >
                  {companyInfo.ctaSecondary}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Clients */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-slate-400 text-center mb-14">
              Trusted by Leading Organizations
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 lg:gap-8">
              {clients.map((client, i) => (
                <ClientLogo key={i} client={client} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services Preview */}
      <section className="bg-[#F8FAFC] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.25em] mb-3">
                Practice Areas
              </p>
              <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-5 tracking-tight">
                Our Practice Areas
              </h2>
              <p className="text-slate-500 leading-relaxed font-light">
                Strategic cybersecurity counsel across compliance, governance, technology, and
                operations, each practice led by experienced advisors with deep domain expertise.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.slice(0, 6).map((service, i) => {
              const Icon = iconMap[service.icon];
              return (
                <Reveal key={service.id} delay={i * 80}>
                  <Link to="/services" className="group block h-full">
                    <div className="bg-white rounded-lg border border-slate-200/80 p-7 h-full hover:border-[#3B82A0]/30 hover:shadow-lg transition-shadow transition-colors duration-300">
                      {Icon && (
                        <Icon
                          className="h-5 w-5 text-[#3B82A0] mb-5"
                          strokeWidth={1.5}
                        />
                      )}
                      <h3 className="text-sm font-medium text-[#0B1D33] mb-2.5 group-hover:text-[#3B82A0] transition-colors duration-200">
                        {service.title}
                      </h3>
                      <p className="text-[13px] text-slate-500 leading-relaxed line-clamp-3 font-light">
                        {service.shortDescription}
                      </p>
                      <div className="mt-5 flex items-center text-[#3B82A0] text-xs font-semibold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        Learn more
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-14 text-center">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-slate-300 text-[#0B1D33] hover:bg-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200"
              >
                View All Practice Areas
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Approach */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.25em] mb-3">
                Our Methodology
              </p>
              <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-5 tracking-tight">
                A Disciplined Approach
              </h2>
              <p className="text-slate-500 leading-relaxed font-light">
                Every engagement follows a structured methodology designed to deliver measurable
                outcomes aligned with your strategic objectives.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {approach.map((step, i) => (
              <Reveal key={step.step} delay={i * 120}>
                <div className="relative">
                  <span className="block text-[3.5rem] leading-none font-extralight text-slate-100 mb-3 tracking-tight">
                    {String(step.step).padStart(2, '0')}
                  </span>
                  <h3 className="text-base font-medium text-[#0B1D33] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[13px] text-slate-500 leading-relaxed font-light">
                    {step.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <Link to="/approach">
              <Button
                variant="outline"
                className="border-slate-300 text-[#0B1D33] hover:bg-slate-50 px-8 h-11 text-sm font-medium rounded transition-colors duration-200"
              >
                Explore Our Approach
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Careers */}
      <section className="bg-[#F8FAFC] py-24 lg:py-32 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="max-w-xl">
                <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.25em] mb-3">
                  Careers
                </p>
                <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-4 tracking-tight">
                  Join Our Team
                </h2>
                <p className="text-slate-500 leading-relaxed font-light">
                  We are always looking for experienced cybersecurity professionals who share our
                  commitment to excellence, integrity, and client impact.
                </p>
              </div>
              <Link to="/careers">
                <Button className="bg-[#0B1D33] hover:bg-[#162D4A] text-white px-8 h-12 text-sm font-medium rounded transition-colors duration-200">
                  View Open Positions
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#0B1D33] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute top-0 left-0 right-0 accent-line" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-light text-white mb-6 tracking-tight">
              Ready to Strengthen Your Security Posture?
            </h2>
            <p className="text-slate-400 text-base max-w-2xl mx-auto mb-10 leading-relaxed font-light">
              Engage with our advisory team to discuss your cybersecurity challenges and explore how
              CISO Dynamics can support your strategic objectives.
            </p>
            <Link to="/contact">
              <Button className="bg-[#3B82A0] hover:bg-[#2D6A85] text-white px-10 h-11 text-[13px] font-normal tracking-wide rounded transition-colors duration-200">
                Engage Our Experts
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
